import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { Loader2, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const intentOptions = [
    "Ik wil een gesprek plannen",
    "Ik wil voorbeeldvideo's ontvangen",
    "Ik wil video's laten maken",
];

const videoTypeOptions = [
    "Verticale video's",
    "Horizontale video's",
    "Beide",
];

const initialForm = {
    name: "",
    email: "",
    company: "",
    listings_per_month: "",
    intent: "",
    video_type: "",
    message: "Ik zou graag meer informatie ontvangen en de mogelijkheden bespreken.",
};

export const BookingDialog = ({ open, onOpenChange }) => {
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(initialForm);

    const update = (k) => (e) =>
        setForm((f) => ({
            ...f,
            [k]: e?.target ? e.target.value : e,
        }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Vul je naam en e-mailadres in.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/leads`, form);
            toast.success(
                "Bedankt! We nemen binnen 24u contact op via marlea.agency@gmail.com."
            );
            setForm(initialForm);
            onOpenChange(false);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Er ging iets mis. Probeer het nogmaals of mail ons rechtstreeks."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="booking-dialog"
                className="bg-white border border-black/10 text-black max-w-lg rounded-3xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle className="font-heading text-3xl font-bold tracking-tighter text-black">
                        Plan mijn gesprek
                    </DialogTitle>
                    <DialogDescription className="text-neutral-600 pt-1">
                        Laat je gegevens achter. We reageren binnen 24 uur via
                        marlea.agency@gmail.com.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={submit} className="mt-4 space-y-4">
                    <div>
                        <Label className="text-[11px] text-neutral-500 uppercase tracking-[0.15em] font-semibold">
                            Volledige naam *
                        </Label>
                        <Input
                            data-testid="booking-name"
                            value={form.name}
                            onChange={update("name")}
                            placeholder="Jouw naam"
                            className="mt-1.5 bg-white border-black/15 text-black placeholder:text-neutral-400 focus-visible:ring-black/20 focus-visible:border-black"
                        />
                    </div>
                    <div>
                        <Label className="text-[11px] text-neutral-500 uppercase tracking-[0.15em] font-semibold">
                            E-mailadres *
                        </Label>
                        <Input
                            data-testid="booking-email"
                            type="email"
                            value={form.email}
                            onChange={update("email")}
                            placeholder="jij@bedrijf.nl"
                            className="mt-1.5 bg-white border-black/15 text-black placeholder:text-neutral-400 focus-visible:ring-black/20 focus-visible:border-black"
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-[11px] text-neutral-500 uppercase tracking-[0.15em] font-semibold">
                                Bedrijf
                            </Label>
                            <Input
                                data-testid="booking-company"
                                value={form.company}
                                onChange={update("company")}
                                placeholder="Optioneel"
                                className="mt-1.5 bg-white border-black/15 text-black placeholder:text-neutral-400 focus-visible:ring-black/20 focus-visible:border-black"
                            />
                        </div>
                        <div>
                            <Label className="text-[11px] text-neutral-500 uppercase tracking-[0.15em] font-semibold">
                                Listings per maand
                            </Label>
                            <Input
                                data-testid="booking-listings"
                                value={form.listings_per_month}
                                onChange={update("listings_per_month")}
                                placeholder="Optioneel"
                                className="mt-1.5 bg-white border-black/15 text-black placeholder:text-neutral-400 focus-visible:ring-black/20 focus-visible:border-black"
                            />
                        </div>
                    </div>

                    <div>
                        <Label className="text-[11px] text-neutral-500 uppercase tracking-[0.15em] font-semibold">
                            Wat wil je doen?
                        </Label>
                        <Select
                            value={form.intent}
                            onValueChange={update("intent")}
                        >
                            <SelectTrigger
                                data-testid="booking-intent"
                                className="mt-1.5 h-10 bg-white border-black/15 text-black focus:ring-black/20"
                            >
                                <SelectValue placeholder="Kies een optie" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-black/15">
                                {intentOptions.map((o) => (
                                    <SelectItem key={o} value={o}>
                                        {o}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label className="text-[11px] text-neutral-500 uppercase tracking-[0.15em] font-semibold">
                            Type video
                        </Label>
                        <Select
                            value={form.video_type}
                            onValueChange={update("video_type")}
                        >
                            <SelectTrigger
                                data-testid="booking-video-type"
                                className="mt-1.5 h-10 bg-white border-black/15 text-black focus:ring-black/20"
                            >
                                <SelectValue placeholder="Kies een optie" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-black/15">
                                {videoTypeOptions.map((o) => (
                                    <SelectItem key={o} value={o}>
                                        {o}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label className="text-[11px] text-neutral-500 uppercase tracking-[0.15em] font-semibold">
                            Bericht
                        </Label>
                        <Textarea
                            data-testid="booking-message"
                            value={form.message}
                            onChange={update("message")}
                            rows={4}
                            className="mt-1.5 bg-white border-black/15 text-black placeholder:text-neutral-400 focus-visible:ring-black/20 focus-visible:border-black resize-none"
                        />
                    </div>

                    <button
                        data-testid="booking-submit"
                        type="submit"
                        disabled={loading}
                        className="btn-shine w-full rounded-full bg-black text-white font-semibold py-4 transition-all duration-300 hover:bg-neutral-900 hover:shadow-[0_10px_30px_-10px_rgba(0,0,0,0.4)] disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                    >
                        {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <ArrowUpRight className="w-4 h-4" />
                        )}
                        Verstuur aanvraag
                    </button>
                    <p className="text-xs text-neutral-500 text-center">
                        Aanvragen komen direct binnen bij Marlea.
                    </p>
                </form>
            </DialogContent>
        </Dialog>
    );
};
